/**
 * Deterministic stub WorkoutSummary list for visual verification.
 *
 * Activated alongside the firehose stub when `?firehoseStub=1` is set.
 * The dates are relative to "now" so the Set-tab month chart always
 * renders a believable 4-week window (current week + 3 prior).
 *
 * Distance + duration values are illustrative, NOT pulled from Scott's
 * real data. The shape matches WorkoutSummary so SetTab / WorkoutList /
 * MetricRings render realistic layouts in the snapshot harness.
 */
import type { WorkoutSummary } from "@contract/types";

function summary(
  daysAgo: number,
  distanceMeters: number,
  durationSec: number,
): WorkoutSummary {
  const start = new Date();
  start.setDate(start.getDate() - daysAgo);
  start.setHours(7, 0, 0, 0); // morning swim
  const end = new Date(start.getTime() + durationSec * 1000);
  const date = start.toISOString().slice(0, 10);
  return {
    uuid: `stub-${date}-${daysAgo}`,
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    activityType: "swimming",
    durationSeconds: durationSec,
    totalDistance: { value: distanceMeters, unit: "m" },
  };
}

/**
 * 12 swims spread across ~28 days. Pattern aims to make the 4-week
 * distance chart visually distinct (rising trend with one peak week).
 *   Week 0 (current): 2 swims, ~4km total
 *   Week -1:          4 swims, ~8km total — peak
 *   Week -2:          3 swims, ~6.5km total
 *   Week -3:          3 swims, ~5.5km total
 */
export function generateStubWorkouts(): WorkoutSummary[] {
  return [
    // Week 0 (current)
    summary(0, 2050, 3300),
    summary(2, 2000, 3200),
    // Week -1 (peak)
    summary(7, 2050, 3300),
    summary(9, 2400, 3700),
    summary(11, 1800, 2900),
    summary(12, 1800, 2900),
    // Week -2
    summary(15, 2400, 3700),
    summary(17, 2050, 3300),
    summary(19, 2000, 3200),
    // Week -3
    summary(22, 2200, 3500),
    summary(24, 2000, 3200),
    summary(26, 1300, 2200),
  ];
}
